/**
 * Multi-Source Reconciliation View
 * =================================
 * Shows P&L line item totals broken down by document source
 * (bank statements, credit card statements, tax returns, P&L statements).
 *
 * This is the core view for P&L reconstruction: accountants can see
 * where each number comes from and identify discrepancies between sources.
 */

import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { CARD_CHARGES_NOTE, CARD_CHARGES_NOTE_SENTENCE } from './cardChargesLabel';
import { FinancialValue } from '../../components/shared/FinancialValue';
import { formatCurrency } from '../../utils/financialFormat';
import {
  AlertTriangle,
  CheckCircle,
  FileText,
  CreditCard,
  Building2,
  Receipt,
  Database,
  ChevronDown,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Minus,
  Download,
} from 'lucide-react';

/** Backend-computed aggregates from pl_aggregate_service.compute_multi_source_aggregates */
interface MSYearAggregates {
  /** { section: { source: float, combined: float, variance: float } } */
  section_totals: Record<string, Record<string, number>>;
  /** Sum across all sections: { source: float, combined: float, variance: float } */
  grand_totals: Record<string, number>;
  /** Alias of grand_totals (expenses stored negative ⟹ grand total IS net income) */
  net_income_by_source: Record<string, number>;
}

interface MultiSourceData {
  report_id: string;
  years: number[];
  sources_found: string[];
  source_labels: Record<string, string>;
  source_document_counts: Record<string, number>;
  source_grids: Record<string, Record<number, Record<string, Record<string, number[]>>>>;
  line_item_comparison: Record<
    number,
    Record<string, Record<string, Record<string, number>>>
  >;
  /** Pre-computed aggregates keyed by year string, e.g. "2022". */
  aggregates?: Record<string, MSYearAggregates>;
  message?: string;
}

interface MultiSourceReconciliationProps {
  reportId: string;
}

const SOURCE_ICONS: Record<string, React.ReactNode> = {
  bank_statements: <Building2 size={16} />,
  credit_card: <CreditCard size={16} />,
  tax_returns: <FileText size={16} />,
  pl_statements: <Receipt size={16} />,
  quickbooks: <Database size={16} />,
  unknown: <AlertTriangle size={16} />,
};

const SOURCE_COLORS: Record<string, React.CSSProperties> = {
  bank_statements: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', borderColor: 'var(--color-needs-human-tint-bd)' },
  credit_card: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', borderColor: 'var(--color-needs-human-tint-bd)' },
  tax_returns: { backgroundColor: 'transparent', color: 'var(--color-verified)', borderColor: 'var(--color-verified)' },
  pl_statements: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', borderColor: 'var(--color-needs-human-tint-bd)' },
  quickbooks: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', borderColor: 'var(--color-needs-human-tint-bd)' },
  unknown: { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)', borderColor: 'var(--color-border)' },
};

const SOURCE_HEADER_COLORS: Record<string, React.CSSProperties> = {
  bank_statements: { backgroundColor: 'var(--color-brand)' },
  credit_card: { backgroundColor: 'var(--color-brand)' },
  tax_returns: { backgroundColor: 'var(--color-verified)' },
  pl_statements: { backgroundColor: 'var(--color-brand)' },
  quickbooks: { backgroundColor: 'var(--color-brand)' },
  unknown: { backgroundColor: 'var(--color-text-dim)' },
  combined: { backgroundColor: 'var(--color-text-dim)' },
  tax_returns_reported: { backgroundColor: 'var(--color-verified)' },
  variance: { backgroundColor: 'var(--color-brand)' },
};

const formatPercent = (value: number): string => {
  if (value === 0) return '-';
  return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
};

const VarianceIndicator: React.FC<{ variance: number; pct: number }> = ({ variance, pct }) => {
  if (Math.abs(variance) < 1) {
    return (
      <span className="inline-flex items-center gap-1 text-xs" style={{ color: 'var(--color-verified)' }}>
        <CheckCircle size={12} />
        Matched
      </span>
    );
  }

  const isOver = variance > 0;
  return (
    <span
      className="inline-flex items-center gap-1 text-xs font-medium"
      style={{
        color:
          Math.abs(pct) > 5
            ? 'var(--color-needs-human-text)'
            : Math.abs(pct) > 2
            ? 'var(--color-brand)'
            : 'var(--color-text-muted)',
      }}
    >
      {isOver ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
      <FinancialValue value={variance} /> ({formatPercent(pct)})
    </span>
  );
};

export default function MultiSourceReconciliation({ reportId }: MultiSourceReconciliationProps) {
  const [data, setData] = useState<MultiSourceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedYear, setSelectedYear] = useState<number>(0);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['INCOME', 'COST OF GOODS SOLD', 'OPERATING EXPENSES'])
  );

  useEffect(() => {
    fetchData();
  }, [reportId]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await api.get(`/api/v1/reconciliation/multi-source/${reportId}`);
      setData(result);
      if (result.years?.length > 0) {
        setSelectedYear(result.years[result.years.length - 1]);
      }
    } catch (err: any) {
      console.error('Error fetching multi-source data:', err);
      setError(err.message || 'Failed to load multi-source reconciliation');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: 'var(--color-brand)' }}></div>
          <p style={{ color: 'var(--color-text-secondary)' }}>Analyzing document sources...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border rounded-lg p-6" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
        <h3 className="font-bold text-lg mb-2" style={{ color: 'var(--color-needs-human-text)' }}>Error</h3>
        <p style={{ color: 'var(--color-needs-human-text)' }}>{error}</p>
      </div>
    );
  }

  if (!data || data.years.length === 0) {
    return (
      <div className="border rounded-lg p-6 text-center" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
        <Database size={48} className="mx-auto mb-4" style={{ color: 'var(--color-text-dim)' }} />
        <h3 className="font-bold text-lg mb-2" style={{ color: 'var(--color-text-primary)' }}>No Source Data Available</h3>
        <p style={{ color: 'var(--color-text-secondary)' }}>
          {data?.message || 'Upload bank statements, tax returns, or credit card statements to enable multi-source reconciliation.'}
        </p>
      </div>
    );
  }

  const { sources_found, source_labels, source_document_counts, line_item_comparison } = data;
  const yearComparison = line_item_comparison[selectedYear] || {};

  // Backend-computed aggregates for selected year (preferred over JS math).
  const yearKey = String(selectedYear);
  const yearAggs: MSYearAggregates | undefined = data.aggregates?.[yearKey];

  // Determine which columns to show
  const activeSources = sources_found.filter((s) => s !== 'unknown');
  const hasTaxReported = Object.values(yearComparison).some((section) =>
    Object.values(section).some((item: any) => item.tax_returns_reported !== undefined)
  );

  // Grand totals for summary cards — prefer backend-computed; fall back to JS for deploy-order safety.
  const grandTotals: Record<string, number> = {};
  if (yearAggs?.grand_totals) {
    for (const source of activeSources) {
      grandTotals[source] = yearAggs.grand_totals[source] ?? 0;
    }
    grandTotals['combined'] = yearAggs.grand_totals['combined'] ?? 0;
  } else {
    for (const source of activeSources) {
      grandTotals[source] = 0;
    }
    grandTotals['combined'] = 0;
    for (const section of Object.values(yearComparison)) {
      for (const item of Object.values(section) as any[]) {
        for (const source of activeSources) {
          grandTotals[source] += item[source] || 0;
        }
        grandTotals['combined'] += item.combined || 0;
      }
    }
  }

  return (
    <div className="space-y-6">
      {/* Source Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {activeSources.map((source) => (
          <div
            key={source}
            className="rounded-lg border p-4"
            style={SOURCE_COLORS[source] || SOURCE_COLORS.unknown}
          >
            <div className="flex items-center gap-2 mb-2">
              {SOURCE_ICONS[source] || SOURCE_ICONS.unknown}
              <span className="text-xs font-bold uppercase tracking-wide">
                {source_labels[source] || source}
              </span>
            </div>
            {source === 'credit_card' && (
              <div
                data-testid="card-charges-note-summary"
                className="text-xs mb-1"
                style={{ color: 'var(--color-text-muted)' }}
              >
                {CARD_CHARGES_NOTE_SENTENCE}
              </div>
            )}
            <div className="text-lg font-bold">
              {source_document_counts[source] || 0} docs
            </div>
            <div className="text-sm font-medium mt-1">
              <FinancialValue value={grandTotals[source]} />
            </div>
          </div>
        ))}
        <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)', borderColor: 'var(--color-border)' }}>
          <div className="flex items-center gap-2 mb-2">
            <Minus size={16} />
            <span className="text-xs font-bold uppercase tracking-wide">Combined</span>
          </div>
          <div className="text-lg font-bold">
            {Object.values(source_document_counts).reduce((a, b) => a + b, 0)} docs
          </div>
          <div className="text-sm font-medium mt-1">
            <FinancialValue value={grandTotals['combined']} />
          </div>
        </div>
      </div>

      {/* Year Selector + Export */}
      <div className="rounded-lg shadow p-4 flex items-center justify-between" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
          Multi-Source P&L Comparison
        </h2>
        <div className="flex gap-2 items-center">
          {data.years.map((year) => (
            <button
              key={year}
              onClick={() => setSelectedYear(year)}
              className="px-4 py-2 rounded-lg font-medium transition-colors"
              style={
                selectedYear === year
                  ? { backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }
                  : { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }
              }
            >
              {year}
            </button>
          ))}
          <a
            href={`/api/v1/reconciliation/multi-source/${reportId}/export`}
            target="_blank"
            rel="noreferrer"
            data-testid="download-multi-source-xlsx"
            className="ml-2 flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg"
            style={{ backgroundColor: 'var(--color-text-primary)', color: 'var(--color-bg-base)' }}
            title="Download the multi-source comparison as an XLSX workbook"
          >
            <Download size={14} />
            Download XLSX
          </a>
        </div>
      </div>

      {/* Comparison Table */}
      <div className="rounded-lg shadow overflow-hidden" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider sticky left-0 z-10 min-w-[200px]" style={{ color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-elevated)' }}>
                  P&L Line Item
                </th>
                {activeSources.map((source) => (
                  <th
                    key={source}
                    className="px-3 py-3 text-right text-xs font-bold uppercase tracking-wider min-w-[130px]"
                    style={{ color: 'var(--color-on-brand)' }}
                  >
                    <div
                      className="rounded px-2 py-1"
                      style={SOURCE_HEADER_COLORS[source] || { backgroundColor: 'var(--color-text-dim)' }}
                      title={source === 'credit_card' ? CARD_CHARGES_NOTE_SENTENCE : undefined}
                    >
                      {source_labels[source] || source}
                    </div>
                    {source === 'credit_card' && (
                      <div
                        data-testid="card-charges-note-header"
                        className="mt-1 text-xs font-normal normal-case tracking-normal"
                        style={{ color: 'var(--color-text-muted)' }}
                      >
                        {CARD_CHARGES_NOTE_SENTENCE}
                      </div>
                    )}
                  </th>
                ))}
                <th className="px-3 py-3 text-right text-xs font-bold uppercase tracking-wider min-w-[130px]" style={{ color: 'var(--color-on-brand)' }}>
                  <div className="rounded px-2 py-1" style={SOURCE_HEADER_COLORS.combined}>
                    Combined
                  </div>
                </th>
                {hasTaxReported && (
                  <th className="px-3 py-3 text-right text-xs font-bold uppercase tracking-wider min-w-[130px]" style={{ color: 'var(--color-on-brand)' }}>
                    <div className="rounded px-2 py-1" style={SOURCE_HEADER_COLORS.tax_returns_reported}>
                      Tax Return
                    </div>
                  </th>
                )}
                <th className="px-3 py-3 text-right text-xs font-bold uppercase tracking-wider min-w-[150px]" style={{ color: 'var(--color-on-brand)' }}>
                  <div className="rounded px-2 py-1" style={SOURCE_HEADER_COLORS.variance}>
                    Variance
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(yearComparison).map(
                ([section, lineItems]: [string, any]) => (
                  <React.Fragment key={section}>
                    {/* Section Header */}
                    <tr
                      className="border-t-2 cursor-pointer"
                      style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}
                      onClick={() => toggleSection(section)}
                    >
                      <td
                        colSpan={activeSources.length + 3 + (hasTaxReported ? 1 : 0)}
                        className="px-4 py-3 text-sm font-bold uppercase tracking-wide sticky left-0"
                        style={{ color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)' }}
                      >
                        <div className="flex items-center gap-2">
                          {expandedSections.has(section) ? (
                            <ChevronDown size={16} />
                          ) : (
                            <ChevronRight size={16} />
                          )}
                          {section}
                        </div>
                      </td>
                    </tr>

                    {/* Line Items */}
                    {expandedSections.has(section) &&
                      Object.entries(lineItems).map(
                        ([lineItem, comparison]: [string, any]) => (
                          <tr
                            key={`${section}-${lineItem}`}
                            className="border-b"
                            style={{ borderColor: 'var(--color-border-faint)' }}
                          >
                            <td className="px-4 py-3 text-sm sticky left-0 font-medium whitespace-nowrap" style={{ color: 'var(--color-text-primary)', backgroundColor: 'var(--color-bg-surface)' }}>
                              {lineItem}
                            </td>
                            {activeSources.map((source) => {
                              const val = comparison[source];
                              return (
                                <td
                                  key={source}
                                  className={`px-3 py-3 text-right text-sm ${
                                    !val ? '' : 'font-medium'
                                  }`}
                                  style={{ color: !val ? 'var(--color-text-dim)' : 'var(--color-text-primary)' }}
                                >
                                  <FinancialValue value={val} />
                                </td>
                              );
                            })}
                            <td className="px-3 py-3 text-right text-sm font-bold" style={{ color: 'var(--color-text-primary)', backgroundColor: 'var(--color-bg-elevated)' }}>
                              <FinancialValue value={comparison.combined} />
                            </td>
                            {hasTaxReported && (
                              <td className="px-3 py-3 text-right text-sm font-medium" style={{ color: 'var(--color-verified)', backgroundColor: 'transparent' }}>
                                <FinancialValue value={comparison.tax_returns_reported} />
                              </td>
                            )}
                            <td className="px-3 py-3 text-right">
                              <VarianceIndicator
                                variance={comparison.variance}
                                pct={comparison.variance_pct || 0}
                              />
                            </td>
                          </tr>
                        )
                      )}

                    {/* Section Total Row */}
                    {expandedSections.has(section) && (
                      <tr className="border-t font-bold" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
                        <td className="px-4 py-3 text-sm sticky left-0" style={{ color: 'var(--color-text-primary)', backgroundColor: 'var(--color-bg-elevated)' }}>
                          Total {section}
                        </td>
                        {activeSources.map((source) => {
                          // Prefer backend section total; fall back to JS.
                          const sectionTotal =
                            yearAggs?.section_totals?.[section]?.[source]
                            ?? (Object.values(lineItems as any).reduce(
                                 (sum: number, item: any) => sum + (item[source] || 0),
                                 0
                               ) as number);
                          return (
                            <td
                              key={source}
                              className="px-3 py-3 text-right text-sm"
                              style={{ color: 'var(--color-text-primary)' }}
                            >
                              <FinancialValue value={sectionTotal} />
                            </td>
                          );
                        })}
                        <td className="px-3 py-3 text-right text-sm" style={{ color: 'var(--color-text-primary)', backgroundColor: 'var(--color-bg-hover)' }}>
                          <FinancialValue
                            value={
                              yearAggs?.section_totals?.[section]?.['combined']
                              ?? (Object.values(lineItems as any).reduce(
                                   (sum: number, item: any) => sum + (item.combined || 0),
                                   0
                                 ) as number)
                            }
                          />
                        </td>
                        {hasTaxReported && (
                          <td className="px-3 py-3 text-right text-sm" style={{ color: 'var(--color-verified)' }}>
                            <FinancialValue
                              value={
                                yearAggs?.section_totals?.[section]?.['tax_returns_reported']
                                ?? (Object.values(lineItems as any).reduce(
                                     (sum: number, item: any) =>
                                       sum + (item.tax_returns_reported || 0),
                                     0
                                   ) as number)
                              }
                            />
                          </td>
                        )}
                        <td className="px-3 py-3 text-right">
                          <VarianceIndicator
                            variance={
                              // Prefer backend; fall back to JS.
                              yearAggs?.section_totals?.[section]?.['variance']
                              ?? (Object.values(lineItems as any).reduce(
                                   (sum: number, item: any) => sum + (item.variance || 0),
                                   0
                                 ) as number)
                            }
                            pct={0}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              )}

              {/* Grand Total Row */}
              <tr className="border-t-2 font-bold" style={{ backgroundColor: 'var(--color-bg-hover)', borderColor: 'var(--color-border)' }}>
                <td className="px-4 py-4 text-sm sticky left-0 uppercase tracking-wide" style={{ color: 'var(--color-text-primary)', backgroundColor: 'var(--color-bg-hover)' }}>
                  Net Income / (Loss)
                </td>
                {activeSources.map((source) => {
                  // Prefer backend net income per source; fall back to JS nested reduce.
                  const netTotal =
                    yearAggs?.net_income_by_source?.[source]
                    ?? Object.values(yearComparison).reduce<number>(
                         (sectionSum, lineItems: any) =>
                           sectionSum +
                           Object.values(lineItems).reduce<number>(
                             (itemSum, item: any) => itemSum + (item[source] || 0),
                             0
                           ),
                         0
                       );
                  return (
                    <td
                      key={source}
                      className="px-3 py-4 text-right text-sm"
                      style={{ color: netTotal >= 0 ? 'var(--color-verified)' : 'var(--color-text-muted)' }}
                    >
                      {formatCurrency(netTotal)}
                    </td>
                  );
                })}
                <td className="px-3 py-4 text-right text-sm" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                  <FinancialValue
                    value={
                      yearAggs?.net_income_by_source?.['combined']
                      ?? Object.values(yearComparison).reduce<number>(
                           (sectionSum, lineItems: any) =>
                             sectionSum +
                             Object.values(lineItems).reduce<number>(
                               (itemSum, item: any) => itemSum + (item.combined || 0),
                               0
                             ),
                           0
                         )
                    }
                  />
                </td>
                {hasTaxReported && (
                  <td className="px-3 py-4 text-right text-sm" style={{ color: 'var(--color-verified)' }}>
                    <FinancialValue
                      value={
                        yearAggs?.net_income_by_source?.['tax_returns_reported']
                        ?? Object.values(yearComparison).reduce<number>(
                             (sectionSum, lineItems: any) =>
                               sectionSum +
                               Object.values(lineItems).reduce<number>(
                                 (itemSum, item: any) =>
                                   itemSum + (item.tax_returns_reported || 0),
                                 0
                               ),
                             0
                           )
                      }
                    />
                  </td>
                )}
                <td className="px-3 py-4 text-right">
                  <VarianceIndicator
                    variance={
                      // Prefer backend; fall back to JS.
                      yearAggs?.net_income_by_source?.['variance']
                      ?? Object.values(yearComparison).reduce<number>(
                           (sectionSum, lineItems: any) =>
                             sectionSum +
                             Object.values(lineItems).reduce<number>(
                               (itemSum, item: any) => itemSum + (item.variance || 0),
                               0
                             ),
                           0
                         )
                    }
                    pct={0}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend */}
      <div className="border rounded-lg p-4" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
        <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--color-needs-human-text)' }}>
          How to Read This View:
        </h3>
        <ul className="text-sm space-y-1" style={{ color: 'var(--color-needs-human-text)' }}>
          <li>
            <strong>Each column</strong> shows the annual total for that P&L line item from a
            specific document source
          </li>
          <li>
            <strong>Combined</strong> is the sum of all transaction sources (bank + credit card)
            {activeSources.includes('credit_card') && (
              <span data-testid="card-charges-note-legend">
                . Credit card amounts are {CARD_CHARGES_NOTE}
              </span>
            )}
          </li>
          {hasTaxReported && (
            <li>
              <strong>Tax Return</strong> column shows the amount reported on the business tax return
              for comparison
            </li>
          )}
          <li>
            <strong>Variance</strong> shows the difference between combined transaction totals and
            the tax return (or expected) amount
          </li>
          <li>
            Variances over 5% are highlighted in <span className="font-medium" style={{ color: 'var(--color-needs-human-text)' }}>red</span>;
            2-5% in <span className="font-medium" style={{ color: 'var(--color-brand)' }}>amber</span>
          </li>
        </ul>
      </div>
    </div>
  );
}
